/**
 * Message types for the app-payment WebView <-> native app bridge (InAppBrowser).
 * Used when the hosted /app-payment page needs the native app to run Google Pay
 * and return the token, since the web Google Pay API does not work in the WebView,
 * and to deliver the already-created order plus the payment essentials the hosted
 * page needs (the order is created on the native side before the WebView opens).
 */
import { CustomerDetails } from '@kotipizzagroup/kotipizza-payments-api-client';
import { OrderType } from '../../../../../types';
import { PaymentMethod, PaymentMethodId } from '../../../types';

export const APP_PAYMENT_BRIDGE_MESSAGE_TYPE = {
  REQUEST_GOOGLE_PAY_TOKEN: 'requestGooglePayToken',
  GOOGLE_PAY_TOKEN_RESPONSE: 'googlePayTokenResponse',
  PAYMENT_HANDOFF: 'paymentHandoff',
  ORDER_PAYMENT_SESSION: 'orderPaymentSession',
  REQUEST_ORDER_PAYMENT_SESSION: 'requestOrderPaymentSession',
  ORDER_PAYMENT_SESSION_ACK: 'orderPaymentSessionAck',
} as const;

export type PaymentHandoffMessage = {
  type: typeof APP_PAYMENT_BRIDGE_MESSAGE_TYPE.PAYMENT_HANDOFF;
  path: string;
};

export function isPaymentHandoffMessage(detail: unknown): detail is PaymentHandoffMessage {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'type' in detail &&
    (detail as PaymentHandoffMessage).type === APP_PAYMENT_BRIDGE_MESSAGE_TYPE.PAYMENT_HANDOFF &&
    typeof (detail as PaymentHandoffMessage).path === 'string'
  );
}

export type RequestGooglePayTokenMessage = {
  type: typeof APP_PAYMENT_BRIDGE_MESSAGE_TYPE.REQUEST_GOOGLE_PAY_TOKEN;
  gatewayMerchantId: string;
  price: number;
  /**
   * Correlates a response with the attempt that asked for it. The hosted page gives up on a
   * request after a timeout and lets the user tap again, but the native Google Pay sheet from
   * the abandoned attempt is still open — without this, finishing it would answer the *new*
   * attempt with the old attempt's token. Optional on the wire because an older installed app
   * will not echo it back; see isGooglePayTokenResponseFor.
   */
  requestId?: string;
};

export type GooglePayTokenResponseMessage = {
  type: typeof APP_PAYMENT_BRIDGE_MESSAGE_TYPE.GOOGLE_PAY_TOKEN_RESPONSE;
  token?: string;
  error?: string;
  cancelled?: boolean;
  requestId?: string;
};

export function isRequestGooglePayTokenMessage(
  detail: unknown
): detail is RequestGooglePayTokenMessage {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'type' in detail &&
    (detail as RequestGooglePayTokenMessage).type ===
      APP_PAYMENT_BRIDGE_MESSAGE_TYPE.REQUEST_GOOGLE_PAY_TOKEN &&
    typeof (detail as RequestGooglePayTokenMessage).gatewayMerchantId === 'string' &&
    typeof (detail as RequestGooglePayTokenMessage).price === 'number'
  );
}

export function isGooglePayTokenResponseMessage(
  detail: unknown
): detail is GooglePayTokenResponseMessage {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'type' in detail &&
    (detail as GooglePayTokenResponseMessage).type ===
      APP_PAYMENT_BRIDGE_MESSAGE_TYPE.GOOGLE_PAY_TOKEN_RESPONSE
  );
}

/**
 * Whether a Google Pay token response answers the given request. A response that carries no
 * requestId comes from an older installed app that does not echo it; accept it, because the
 * alternative is breaking Google Pay for every one of those installs. A response that carries
 * a different one belongs to an attempt this page already gave up on — drop it, or the user
 * pays with a token built for a superseded request.
 */
export function isGooglePayTokenResponseFor(
  detail: unknown,
  requestId: string
): detail is GooglePayTokenResponseMessage {
  if (!isGooglePayTokenResponseMessage(detail)) return false;

  return detail.requestId === undefined || detail.requestId === requestId;
}

/** Minimal restaurant fields the hosted page needs for display + shopExternalId. */
export type OrderPaymentSessionRestaurant = {
  externalId: string;
  displayName: string;
  streetAddress: string;
  zipCode: string;
  city: string;
};

/**
 * Delivered from native to the hosted /app-payment page. Carries the already-created
 * order id/key plus everything the payment methods need, so the hosted page never has
 * to read OrderContext. Replaces the previous full serialized-order payload.
 */
export type OrderPaymentSessionMessage = {
  type: typeof APP_PAYMENT_BRIDGE_MESSAGE_TYPE.ORDER_PAYMENT_SESSION;
  orderId: string;
  orderKey: string;
  grandTotal: number;
  shopExternalId: string;
  customerDetails: CustomerDetails;
  paymentMethodId: PaymentMethodId;
  paymentMethods: PaymentMethod[];
  orderType: OrderType | null;
  preorderTime: string | null;
  deliveryAddress: string;
  orderEstimateMinutes: number | null;
  restaurant: OrderPaymentSessionRestaurant | null;
};

export function isOrderPaymentSessionMessage(
  detail: unknown
): detail is OrderPaymentSessionMessage {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'type' in detail &&
    (detail as OrderPaymentSessionMessage).type ===
      APP_PAYMENT_BRIDGE_MESSAGE_TYPE.ORDER_PAYMENT_SESSION &&
    typeof (detail as OrderPaymentSessionMessage).orderId === 'string' &&
    typeof (detail as OrderPaymentSessionMessage).orderKey === 'string' &&
    // Non-empty, not merely a string: every payment method charges against this shop id, so an
    // empty one produces a form that cannot complete. Rejecting it here surfaces the hosted
    // page's start error instead of a dead "add gift card" button on a created order.
    Boolean((detail as OrderPaymentSessionMessage).shopExternalId) &&
    typeof (detail as OrderPaymentSessionMessage).shopExternalId === 'string' &&
    typeof (detail as OrderPaymentSessionMessage).grandTotal === 'number'
  );
}

/**
 * Sent by the hosted page once it is actually listening, and again is never needed after that.
 * The native side posts the session eagerly and on browserPageLoaded, but neither event lines
 * up with React hydration — this one does, so a page that took longer to boot than the repost
 * window still gets its session.
 */
export type RequestOrderPaymentSessionMessage = {
  type: typeof APP_PAYMENT_BRIDGE_MESSAGE_TYPE.REQUEST_ORDER_PAYMENT_SESSION;
};

export function isRequestOrderPaymentSessionMessage(
  detail: unknown
): detail is RequestOrderPaymentSessionMessage {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'type' in detail &&
    (detail as RequestOrderPaymentSessionMessage).type ===
      APP_PAYMENT_BRIDGE_MESSAGE_TYPE.REQUEST_ORDER_PAYMENT_SESSION
  );
}

/** Sent by the hosted page when it has accepted the session, so the native side can stop reposting. */
export type OrderPaymentSessionAckMessage = {
  type: typeof APP_PAYMENT_BRIDGE_MESSAGE_TYPE.ORDER_PAYMENT_SESSION_ACK;
  orderId: string;
};

export function isOrderPaymentSessionAckMessage(
  detail: unknown
): detail is OrderPaymentSessionAckMessage {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'type' in detail &&
    (detail as OrderPaymentSessionAckMessage).type ===
      APP_PAYMENT_BRIDGE_MESSAGE_TYPE.ORDER_PAYMENT_SESSION_ACK &&
    typeof (detail as OrderPaymentSessionAckMessage).orderId === 'string'
  );
}

/** Shape of the native AppGooglePay plugin result when parsing the token. */
export type AppGooglePayTokenResponse = {
  paymentMethodData: {
    tokenizationData: {
      token: string;
    };
  };
};
